import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { reviseHistoryLog } from "apis/controllers/applications";
import { FileGroup } from "common/FileUpload";
import { FileProvider, useFileContext } from "context/FilesV2";
import { useLoading } from "hooks/useLoading";
import { useSnackbar } from "notistack";
import { useEffect } from "react";
import { deleteBundle, deleteFile, downloadFile, uploadFiles } from "util";
import { v4 as uuidv4 } from "uuid";

const PROJECT_TABLE_ID = 4;

const getFileTypeId = (row) =>
  row?.fileTypeId ?? row?.fileType?.type ?? row?.fileType?.id;

const getFileTypeTitle = (row) =>
  row?.fileType?.descr ?? row?.fileTypeDescr ?? "Upload file";

const getHistoryFileGroupFields = (row) => {
  const fileType = row?.fileType;
  const commentsField = {
    type: "string",
    name: "comments",
    label:
      row?.commentsLabel ?? row?.lblComments ?? fileType?.lblComments ?? "Σχόλια",
    isRequired: false,
  };

  if (!fileType) {
    return [commentsField];
  }

  return [
    {
      type: "dropdown",
      name: "common",
      label: fileType.lkCommon?.lbl,
      options: fileType.lkCommon?.lookup?.map((item) => ({
        value: item.value,
        label: item.label,
      })),
      isRequired: true,
    },
    {
      type: "date",
      name: "dateFrom",
      label: fileType.lblDateFrom,
      isRequired: true,
    },
    {
      type: "date",
      name: "dateTo",
      label: fileType.lblDateTo,
      isRequired: true,
    },
    {
      type: "date",
      name: "issuedAt",
      label: fileType.lblIssuedAt,
      isRequired: true,
    },
    {
      type: "string",
      name: "issuedBy",
      label: fileType.lblIssuedBy,
      isRequired: true,
    },
    {
      type: "string",
      name: "number",
      label: fileType.lblNumber,
      isRequired: true,
    },
    commentsField,
    {
      type: "boolean",
      name: "checkboxChecked",
      label: fileType.lblCheckbox,
    },
  ].filter((field) => !!field.label);
};

const createHistoryFileGroup = (row) => {
  const fileTypeId = getFileTypeId(row);
  if (fileTypeId == null) return [];

  return [
    {
      id: fileTypeId,
      title: getFileTypeTitle(row),
      fields: getHistoryFileGroupFields(row),
      bundles: [
        {
          id: uuidv4(),
          files: [],
          fileType: fileTypeId,
          values: {
            comments: row?.comments ?? "",
            common: row?.common ?? null,
            dateFrom: row?.dateFrom ?? null,
            dateTo: row?.dateTo ?? null,
            issuedAt: row?.issuedAt ?? null,
            issuedBy: row?.issuedBy ?? "",
            number: row?.number ?? "",
            checkboxChecked: row?.checkboxChecked ?? false,
          },
          saved: false,
        },
      ],
      templates: row?.fileType?.templates ?? [],
      maxBundles: row?.fileType?.max ?? 1,
    },
  ];
};

const getBundleIds = (fileGroups) =>
  fileGroups.reduce((acc, group) => {
    return [
      ...acc,
      ...(group?.bundles ?? [])
        .filter((bundle) => bundle?.files?.length > 0)
        .map((bundle) => bundle.id),
    ];
  }, []);

const hasSelectedFiles = (fileGroups) =>
  fileGroups.some((group) =>
    group?.bundles?.some((bundle) => bundle?.files?.length > 0)
  );

const getLogId = (row) => row?.id ?? row?.logId ?? row?.eventLogId;

const getComment = (fileGroups) =>
  fileGroups
    .flatMap((group) => group?.bundles ?? [])
    .find((bundle) => bundle?.files?.length > 0)
    ?.values?.comments ?? null;

const decodeHtmlMessage = (message) =>
  message
    ?.toString()
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, "&") ?? "";

const getSnackbarHtmlMessage = (message) => (
  <span
    style={{ display: "block", lineHeight: 1.5 }}
    dangerouslySetInnerHTML={{ __html: decodeHtmlMessage(message) }}
  />
);

const submitApplicationRevision = ({ logId, bundleIds, comment, projectTableId }) =>
  reviseHistoryLog({
    LogId: logId,
    BundleIds: bundleIds,
    Comment: comment,
    projectTableId,
  });

const HistoryFileUploadDialog = ({
  open,
  row,
  applicationId,
  projectTableId = PROJECT_TABLE_ID,
  onSubmitRevision = submitApplicationRevision,
  onClose,
  onSuccess,
}) => {
  const recordGuid = applicationId ?? null;
  const resolvedProjectTableId = row?.projectTableId ?? projectTableId;

  return (
    <FileProvider
      downloadFile={(fileId, type) => downloadFile(fileId, type)}
      uploadFiles={(fileGroups) => uploadFiles(fileGroups, null)}
      onDeleteBundle={async (bundleId) =>
        await deleteBundle(bundleId, resolvedProjectTableId, null, recordGuid, null)
      }
      onDeleteFile={async (fileId) => {
        await deleteFile(fileId, resolvedProjectTableId, null, recordGuid, null);
      }}
    >
      <HistoryFileUploadDialogContent
        open={open}
        row={row}
        projectTableId={resolvedProjectTableId}
        onSubmitRevision={onSubmitRevision}
        onClose={onClose}
        onSuccess={onSuccess}
      />
    </FileProvider>
  );
};

const HistoryFileUploadDialogContent = ({
  open,
  row,
  projectTableId,
  onSubmitRevision,
  onClose,
  onSuccess,
}) => {
  const { fileGroups, control } = useFileContext();
  const { setLoading } = useLoading();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (!open || !row) {
      control.initialize([]);
      return;
    }

    control.initialize(createHistoryFileGroup(row));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, row]);

  const handleClose = () => {
    control.initialize([]);
    onClose();
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const logId = getLogId(row);
      if (!logId) {
        enqueueSnackbar("Δε βρέθηκε το αναγνωριστικό του ιστορικού.", {
          variant: "error",
        });
        return;
      }

      const uploadResponse = await control.uploadFileGroups();
      if (uploadResponse?.status === "error") {
        enqueueSnackbar(
          getSnackbarHtmlMessage(
            uploadResponse?.message ?? "Η μεταφόρτωση του αρχείου απέτυχε."
          ),
          { variant: "error" }
        );
        return;
      }

      const bundleIds = getBundleIds(fileGroups);
      const res = await onSubmitRevision({
        logId,
        projectTableId,
        bundleIds,
        comment: getComment(fileGroups),
      });

      if (res?.status === "error" || res?.succeeded === false) {
        enqueueSnackbar(
          getSnackbarHtmlMessage(
            res?.message ?? "Η ορθή επανάληψη δεν πραγματοποιήθηκε."
          ),
          { variant: "error" }
        );
        return;
      }

      control.setIsDirty(false);
      await onSuccess?.();
      handleClose();
      if (res?.message) enqueueSnackbar(res.message, { variant: "success" });
    } catch (error) {
      enqueueSnackbar(
        getSnackbarHtmlMessage(
          error?.message ?? "Η ορθή επανάληψη δεν πραγματοποιήθηκε."
        ),
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Typography variant="h4">
          {row?.fileUploadTitle ?? "Μεταφόρτωση αρχείου"}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {fileGroups?.map((group) => (
          <FileGroup
            key={group.id}
            reviewMode={false}
            isReadOnly={false}
            canAdd
            id={group.id}
            title={group?.title}
            maxSize={row?.maxSize ?? 6291456}
            maxFiles={row?.maxFiles ?? 5}
            maxBundles={1}
            fields={group.fields}
            bundles={group?.bundles}
            templates={group?.templates}
            accept={row?.accept ?? null}
          />
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="error" variant="contained">
          Ακύρωση
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={!hasSelectedFiles(fileGroups)}
        >
          Αποθήκευση
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HistoryFileUploadDialog;
